import {
	IntegrationFlowCapabilityKind,
	IntegrationFlowKind
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import {
	Dialog,
	FontIconName,
	Toolbar
} from "client/Components";
import {
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import styles from "./TypeSelectionDialog.module.less";
import { useDataInputDialog } from "client/Hooks";
import {
	integrationFlowKindToCapabilities,
	integrationFlowKindToLocalizationKey
} from "./common";
import { never } from "client_legacy/ndaw/errors";
import { CapabilityCard } from "./CapabilityCard";
import { SelectionCard } from "./SelectionCard";
import { useExamplesDialog } from "./ExamplesDialog";

export type IntegrationFlowKindSelection = {
	kind: IntegrationFlowKind;
	capabilities: readonly IntegrationFlowCapabilityKind[];
};

export type TypeSelectionDialogProps = {
	creatableKinds: readonly IntegrationFlowKind[];
	onSubmit: (selection: IntegrationFlowKindSelection) => void;
	onClose: () => void;
};

export const TypeSelectionDialog = (props: TypeSelectionDialogProps): ReactElement => {
	const {
		creatableKinds,
		onSubmit,
		onClose
	} = props;

	const [selectedKind, setSelectedKind] = useState<IntegrationFlowKind | null>(null);
	const [selectedCapabilityKinds, setSelectedCapabilityKinds] = useState<ReadonlySet<IntegrationFlowCapabilityKind>>(
		() => new Set<IntegrationFlowCapabilityKind>()
	);

	const [examplesDialog, openExamplesDialog] = useExamplesDialog();

	const kindSelectedHandler = useCallback(
		(kind: IntegrationFlowKind): void => {
			setSelectedKind(kind);
			setSelectedCapabilityKinds(
				new Set<IntegrationFlowCapabilityKind>()
			);
		},
		[]
	);

	const submitHandler = useCallback(
		(): void => {
			if (isNil(selectedKind)) {
				throw new Error("No kind selected");
			}

			const {
				optional,
				required
			} = integrationFlowKindToCapabilities[selectedKind];

			onSubmit({
				kind: selectedKind,
				capabilities: [
					...required,
					...optional.filter(x => selectedCapabilityKinds.has(x))
				]
			});
		},
		[
			selectedCapabilityKinds,
			selectedKind,
			onSubmit
		]
	);

	const toggleCapability = useCallback(
		(capabilityKind: IntegrationFlowCapabilityKind): void => setSelectedCapabilityKinds(
			(current): ReadonlySet<IntegrationFlowCapabilityKind> => {
				const result = new Set(current);

				if (!result.delete(capabilityKind)) {
					result.add(capabilityKind);
				}

				return result;
			}
		),
		[]
	);

	const cardsToRender = useMemo(
		(): readonly CardData[] => {
			return kindsInDisplayOrder
				.filter(kind => creatableKinds.includes(kind))
				.map(getCardForKind);
		},
		[creatableKinds]
	);

	return (
		<>
			<Dialog
				trackingId="integration-builder-type-selection-dialog"
				onClose={onClose}
			>
				<Dialog.Header
					title={localize("_SelectItemType")}
					onClose={onClose}
				/>
				<Dialog.Body>
					<ul className={styles.list}>
						{cardsToRender.map(
							(card): ReactElement => (
								<li
									key={card.kind}
									className={styles.item}
								>
									<SelectionCard
										icon={card.icon}
										title={localize(card.title)}
										description={localize(card.description)}
										isSelected={selectedKind === card.kind}
										onClick={() => kindSelectedHandler(card.kind)}
										trackingId={generateTrackingId("integration-type", `|${card.kind}`)}
										onViewExamples={() => openExamplesDialog(card.kind)}
									/>
								</li>
							)
						)}
					</ul>
					{isNotNil(selectedKind) && hasCapabilitiesToChoose(selectedKind) && (
						<section className={styles.capabilitySection}>
							<h4>{localize("_DoYouWantToMakeTheResultSearchable")}</h4>
							<div className={styles.capabilityList}>
								{integrationFlowKindToCapabilities[selectedKind].optional
									.map(
										(capabilityKind): ReactElement => {
											const capability: CapabilityCardData = getCapabilityCard(capabilityKind);

											return (
												<CapabilityCard
													key={capabilityKind}
													icon={capability.icon}
													title={localize(capability.title)}
													description={localize(capability.description)}
													isSelected={selectedCapabilityKinds.has(capabilityKind)}
													onChange={() => toggleCapability(capabilityKind)}
													trackingId={generateTrackingId("capability", `|${capabilityKind}`)}
												/>
											);
										}
									)
								}
							</div>
						</section>
					)}
				</Dialog.Body>
				<Dialog.Footer>
					<Toolbar>
						<Toolbar.Button
							trackingId="choose-type"
							text={localize("_ChooseType")}
							kind="primary"
							onClick={submitHandler}
							isDisabled={isNil(selectedKind)}
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{examplesDialog}
		</>
	);
};
TypeSelectionDialog.displayName = "TypeSelectionDialog";

function hasCapabilitiesToChoose(kind: IntegrationFlowKind): boolean {
	return integrationFlowKindToCapabilities[kind]
		.optional
		.length > 0;
}

const kindsInDisplayOrder: readonly IntegrationFlowKind[] = [
	IntegrationFlowKind.General,
	IntegrationFlowKind.Planning
];

type CardData = {
	kind: IntegrationFlowKind;
	icon: FontIconName;
	title: LocalizationKey;
	description: LocalizationKey;
};

function getCardForKind(kind: IntegrationFlowKind): CardData {
	switch (kind) {
		case IntegrationFlowKind.General:
			return {
				kind: IntegrationFlowKind.General,
				icon: "list",
				title: integrationFlowKindToLocalizationKey[IntegrationFlowKind.General],
				description: "_UseForGeneralResults"
			};
		case IntegrationFlowKind.Planning:
			return {
				kind: IntegrationFlowKind.Planning,
				icon: "calendar-days",
				title: integrationFlowKindToLocalizationKey[IntegrationFlowKind.Planning],
				description: "_UseForItemsWithAStartAndEndTime"
			};
		default:
			never(kind);
	}
}

type CapabilityCardData = {
	icon: FontIconName;
	title: LocalizationKey;
	description: LocalizationKey;
};

function getCapabilityCard(kind: IntegrationFlowCapabilityKind): CapabilityCardData {
	switch (kind) {
		case IntegrationFlowCapabilityKind.Search:
			return {
				icon: "magnifying-glass",
				title: "_Search",
				description: "_UseThisForGlobalSearchAndSearchableLiveTiles"
			};
		case IntegrationFlowCapabilityKind.DateRange:
			return {
				icon: "calendar-days",
				title: "_DateRange",
				description: "_UseThisToFilterItemsByAStartAndEndDate"
			};
		default:
			never(kind);
	}
}

type TypeSelectionDialogInitialData = Omit<TypeSelectionDialogProps, "onClose" | "onSubmit">;

export function useTypeSelectionDialog(): [
	element: ReactElement | null,
	open: (props: TypeSelectionDialogInitialData) => Promise<TryResult<IntegrationFlowKindSelection>>
] {
	return useDataInputDialog(
		(props, onClose, onSubmit) => (
			<TypeSelectionDialog
				{...props}
				onClose={onClose}
				onSubmit={onSubmit}
			/>
		)
	);
}
